import { useEffect, useState } from "react";
import { Alert, Modal, Spin, message } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import { brand, neutral, radius } from "../../../theme/tokens";

const CardBody = ({ label, count, hint }) => (
  <>
    <div style={{ fontSize: 13, color: neutral[600] }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.3, color: neutral[900] }}>
      {count.toLocaleString("vi-VN")}
      {/* Đếm theo KHOẢN THU, không theo khách: khách nhiều tài sản có nhiều khoản, mỗi khoản một tin */}
      <span style={{ fontSize: 13, fontWeight: 400, color: neutral[500] }}> khoản thu</span>
    </div>
    <div style={{ fontSize: 12, color: neutral[500], marginTop: 2 }}>{hint}</div>
  </>
);

const cardBase = { padding: "14px 16px", borderRadius: radius.lg, textAlign: "left" };

/** Thẻ chọn một cách gửi — chọn bằng cả thẻ, chỉ một thẻ được chọn tại một thời điểm. */
const OptionCard = ({ active, onSelect, ...body }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onSelect}
    style={{
      ...cardBase,
      position: "relative",
      border: `1.5px solid ${active ? brand.primary : neutral[200]}`,
      background: active ? brand.primarySubtle : neutral[0],
      cursor: "pointer",
      transition: "border-color 0.15s, background 0.15s",
    }}
  >
    {active && (
      <CheckCircleFilled
        style={{ position: "absolute", top: 12, right: 12, fontSize: 16, color: brand.primary }}
      />
    )}
    <CardBody {...body} />
  </button>
);

/** Chỉ có một cách gửi có nghĩa — hiện thông tin, không bắt chọn. */
const InfoCard = (body) => (
  <div style={{ ...cardBase, border: `1px solid ${neutral[200]}`, background: neutral[50] }}>
    <CardBody {...body} />
  </div>
);

/*
 * Hộp thoại gửi tin ZNS cho CẢ ĐỢT (đợt thường, đợt con, hoặc mọi đợt con của đợt cha).
 *
 * Thay cho cặp nút "Gửi lần đầu" / "Gửi lại" — hai cách gửi vẫn giữ nguyên ở
 * backend, chỉ gộp chỗ bấm và hiện trước số tin sẽ gửi:
 *
 *  - Gửi lần đầu: chỉ người CHƯA nhận tin. Bấm lặp không ai nhận trùng.
 *  - Gửi lại    : MỌI khoản chưa đóng, gồm cả người chưa nhận lần nào — không
 *                 riêng người đã nhận.
 *
 * Bản cũ bấm "Gửi lần đầu" là xếp hàng gửi ngay, không hỏi lại câu nào.
 */

const ACTIONS = {
  collection: {
    count: mapZnsAPIWithErrorHandling.sendableCount,
    send: mapZnsAPIWithErrorHandling.sendAllZns,
    resend: mapZnsAPIWithErrorHandling.resendAllZns,
  },
  parent: {
    count: mapZnsAPIWithErrorHandling.sendableCountParent,
    send: mapZnsAPIWithErrorHandling.sendAllParent,
    resend: mapZnsAPIWithErrorHandling.resendAllParent,
  },
};

/**
 * @param {{ target: { id: number, type: "collection"|"parent", name?: string } | null, onClose: () => void }} props
 *   `target = null` là đóng.
 */
export default function SendZnsDialog({ target, onClose }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!target) return undefined;
    let stale = false;
    setSummary(null);
    setError(null);
    setMode(null);

    ACTIONS[target.type]
      .count(target.id)
      .then((res) => {
        if (stale) return;
        const data = res?.data?.data;
        setSummary(data);
        // Còn người chưa nhận thì mặc định "Gửi lần đầu" (không ai nhận trùng).
        // Ai cũng đã nhận rồi thì chỉ còn "Gửi lại" — nút OK ghi rõ "Gửi lại N tin".
        if (data?.unsent > 0) setMode("send");
        else if (data?.all > 0) setMode("resend");
        else setMode(null);
      })
      .catch((e) => !stale && setError(e.message));

    return () => {
      stale = true;
    };
  }, [target]);

  const count = mode === "send" ? summary?.unsent : mode === "resend" ? summary?.all : 0;

  const handleOk = async () => {
    setSubmitting(true);
    try {
      const res = await ACTIONS[target.type][mode](target.id);
      message.success(res?.data?.message ?? "Hệ thống ghi nhận. Đang chờ xử lý!");
      onClose();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (error) return <Alert type="error" showIcon message={error} />;
    if (!summary) return <Spin style={{ display: "block", margin: "24px auto" }} />;
    if (summary.blocked_reason) return <Alert type="warning" showIcon message={summary.blocked_reason} />;
    if (summary.all === 0) {
      return <Alert type="info" showIcon message="Không còn khoản thu nào cần gửi tin (đã thanh toán hết hoặc không còn số tiền phải nộp)." />;
    }

    // Chưa ai nhận tin: "tất cả chưa đóng" CHÍNH LÀ "chưa nhận tin" — hai lựa chọn
    // gửi y hệt nhau, chỉ hiện một.
    if (summary.already_sent === 0) {
      return <InfoCard label="Chưa nhận tin" count={summary.unsent} hint="Lần đầu gửi tin cho đợt này" />;
    }
    // Ai cũng đã nhận: chỉ còn gửi lại.
    if (summary.unsent === 0) {
      return <InfoCard label="Đã nhận tin, chưa đóng" count={summary.all} hint="Tất cả đã nhận tin — gửi lại lần nữa" />;
    }

    return (
      <div
        role="radiogroup"
        aria-label="Chọn cách gửi"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}
      >
        <OptionCard
          active={mode === "send"}
          label="Chưa nhận tin"
          count={summary.unsent}
          hint="Chỉ gửi khoản chưa nhận tin lần nào"
          onSelect={() => setMode("send")}
        />
        <OptionCard
          active={mode === "resend"}
          label="Tất cả chưa đóng"
          count={summary.all}
          hint={`Gồm ${summary.already_sent.toLocaleString("vi-VN")} khoản đã nhận tin`}
          onSelect={() => setMode("resend")}
        />
      </div>
    );
  };

  return (
    <Modal
      open={!!target}
      title={target?.name ? `Gửi tin ZNS — ${target.name}` : "Gửi tin ZNS"}
      onCancel={onClose}
      onOk={handleOk}
      okText={count > 0 ? `${mode === "resend" ? "Gửi lại" : "Gửi"} ${count.toLocaleString("vi-VN")} tin` : "Gửi"}
      cancelText="Hủy"
      okButtonProps={{ disabled: !mode || !count }}
      confirmLoading={submitting}
      maskClosable={false}
      destroyOnClose
      centered
      width="min(480px, 100vw)"
    >
      {renderBody()}
    </Modal>
  );
}
